/** Retrieve properties for a specific owner with pagination support */
import { useCallback, useEffect, useState } from 'react';
import api from '../api';

export interface IOwnerPropertiesResult {
  property_id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  company_name: string;
  company_id: string;
  ownership_start: string;
  ownership_end: string;
}

export interface IOwnerPropertiesParams {
  page?: number;
  rowsPerPage?: number;
  currentOnly?: boolean;
}

export interface IOwnerPropertiesResponse {
  data: IOwnerPropertiesResult[];
  page: number;
  rowsPerPage: number;
  count: number;
  totalPages: number;
}

interface IUseOwnerPropertiesReturn {
  // Data
  properties: IOwnerPropertiesResult[];

  // Pagination info
  currentPage: number;
  rowsPerPage: number;
  totalCount: number;
  totalPages: number;

  // Loading states
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  fetchAllPropertiesHasLoaded: boolean;

  // Actions
  fetchProperties: (params?: IOwnerPropertiesParams) => Promise<void>;
  fetchNextPage: () => Promise<void>;
  fetchPreviousPage: () => Promise<void>;
  fetchAllProperties: () => Promise<void>;
  setPageSize: (pageSize: number) => Promise<void>;
  toggleCurrentOnly: () => Promise<void>;

  // Computed
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const useOwnerProperties = (ownerId: string | null): IUseOwnerPropertiesReturn => {
  // Data state
  const [properties, setProperties] = useState<IOwnerPropertiesResult[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentOnly, setCurrentOnly] = useState<boolean>(true);
  const [fetchAllPropertiesHasLoaded, setFetchAllPropertiesHasLoaded] = useState<boolean>(false);

  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch properties function
  const fetchProperties = useCallback(
    async (params: IOwnerPropertiesParams = {}) => {
      if (!ownerId) {
        setProperties([]);
        return;
      }

      const {
        page = currentPage,
        rowsPerPage: pageSize = rowsPerPage,
        currentOnly: onlyCurrent = currentOnly
      } = params;

      const isNewRequest = page === 0;

      if (isNewRequest) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      setError(null);

      try {
        const response: IOwnerPropertiesResponse = await api.getOwnerProperties(ownerId, {
          page,
          rowsPerPage: pageSize,
          currentOnly: onlyCurrent
        });

        // Update pagination info
        setCurrentPage(response.page);
        setRowsPerPage(response.rowsPerPage);
        setTotalCount(response.count);
        setTotalPages(response.totalPages);
        setCurrentOnly(onlyCurrent);

        // Update properties data
        setProperties(response.data);
      } catch (err) {
        setError((err as Error).message);
        setProperties([]);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [ownerId, currentPage, rowsPerPage, currentOnly]
  );

  // Fetch next page
  const fetchNextPage = useCallback(async () => {
    if (currentPage < totalPages - 1) {
      await fetchProperties({ page: currentPage + 1 });
    }
  }, [currentPage, totalPages, fetchProperties]);

  const fetchPreviousPage = useCallback(async () => {
    if (currentPage > 0) {
      await fetchProperties({ page: currentPage - 1 });
    }
  }, [currentPage, fetchProperties]);

  // Set page size
  const setPageSize = useCallback(
    async (pageSize: number) => {
      await fetchProperties({ page: 0, rowsPerPage: pageSize });
    },
    [fetchProperties]
  );

  // Toggle current only filter
  const toggleCurrentOnly = useCallback(async () => {
    await fetchProperties({ page: 0, currentOnly: !currentOnly });
  }, [currentOnly, fetchProperties]);

  const fetchAllProperties = useCallback(async () => {
    if (!ownerId) {
      setProperties([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response: IOwnerPropertiesResponse = await api.getAllOwnerProperties(ownerId);

      // Update pagination info
      setCurrentPage(response.page);
      setRowsPerPage(response.rowsPerPage);
      setTotalCount(response.count);
      setTotalPages(response.totalPages);

      // Update properties data
      setProperties(response.data);
    } catch (err) {
      setError((err as Error).message);
      setProperties([]);
    } finally {
      setFetchAllPropertiesHasLoaded(true);
      setIsLoading(false);
    }
  }, [ownerId]);

  // Initial fetch when ownerId changes
  useEffect(() => {
    if (ownerId) {
      fetchProperties({ page: currentPage });
    } else {
      setProperties([]);
      setCurrentPage(0);
      setTotalCount(0);
      setTotalPages(0);
    }
  }, [ownerId, fetchProperties, currentPage]);

  return {
    // Data
    properties,

    // Pagination info
    currentPage,
    rowsPerPage,
    totalCount,
    totalPages,

    // Loading states
    isLoading,
    isLoadingMore,
    error,
    fetchAllPropertiesHasLoaded,

    // Actions
    fetchProperties,
    fetchNextPage,
    fetchPreviousPage,
    fetchAllProperties,
    setPageSize,
    toggleCurrentOnly,

    // Computed
    hasNextPage: currentPage < totalPages - 1,
    hasPreviousPage: currentPage > 0
  };
};

export default useOwnerProperties;
